import {
  DEFAULT_FEATURE_AREA_ESTIMATES,
  FeatureAreaKey,
  Improvements,
  ImprovementsSelectedSources,
  PropertyData,
  SubjectData,
  Tkv2PublicRecordJSON,
  schSourceType,
} from '@aloft/toolkit-shared';

import {
  applyEstimatedFeatureArea,
  getPublicRecordFeatureArea,
} from '../../PropertyGrid/utils/applyEstimatedFeatureArea.ts';

type FeatureAreas = Partial<Record<FeatureAreaKey, number>>;

const FEATURE_KEYS = [
  'garage',
  'detachedGarage',
  'carport',
  'attic',
  'barn',
  'pool',
  'spa',
  'porch',
  'patio',
  'deck',
  'shed',
  'workshop',
] as const satisfies readonly FeatureAreaKey[];

const applyDefaultsToImprovements = (
  improvements: Improvements,
  selectedSources: ImprovementsSelectedSources | undefined,
  defaultAreas: FeatureAreas,
  publicRecord: Tkv2PublicRecordJSON | undefined,
  onlyMissing: boolean,
) => {
  let nextImprovements = improvements;
  let nextSelectedSources = selectedSources;
  let didApply = false;

  for (const key of FEATURE_KEYS) {
    if (onlyMissing && nextImprovements[key]?.area) continue;

    const before = nextImprovements[key]?.area;
    const beforeSource = nextSelectedSources?.[key]?.area?.source;
    ({ improvements: nextImprovements, selectedSources: nextSelectedSources } =
      applyEstimatedFeatureArea({
        improvements: nextImprovements,
        selectedSources: nextSelectedSources,
        feature: key,
        defaultAreas,
        sourceAreas: [
          {
            source: schSourceType.enum.abode_public_record,
            area: getPublicRecordFeatureArea(publicRecord, key),
          },
        ],
      }));
    if (
      nextImprovements[key]?.area !== before ||
      nextSelectedSources?.[key]?.area?.source !== beforeSource
    ) {
      didApply = true;
    }
  }

  return { improvements: nextImprovements, selectedSources: nextSelectedSources, didApply };
};

export const applyDefaultFeatureAreas = (
  property: PropertyData,
  defaultAreas?: FeatureAreas,
  { onlyMissing = false }: { onlyMissing?: boolean } = {},
): PropertyData => {
  const areas = { ...DEFAULT_FEATURE_AREA_ESTIMATES, ...defaultAreas };
  const publicRecord = property.readonlyData?.sources?.abode_public_record;

  const main = applyDefaultsToImprovements(
    property.editableData.improvements,
    property.selectedSources?.editableData?.improvements,
    areas,
    publicRecord,
    onlyMissing,
  );

  let didApply = main.didApply;
  let nextProperty: PropertyData = property;

  if (main.didApply) {
    nextProperty = {
      ...property,
      editableData: {
        ...property.editableData,
        improvements: main.improvements,
      },
      selectedSources: {
        ...property.selectedSources,
        editableData: {
          ...property.selectedSources?.editableData,
          improvements: main.selectedSources,
        },
      },
    };
  }

  const subjectEditable = nextProperty.editableData as SubjectData['editableData'];
  if (subjectEditable.arv?.improvements) {
    const arv = applyDefaultsToImprovements(
      subjectEditable.arv.improvements,
      nextProperty.selectedSources?.editableData?.arv?.improvements,
      areas,
      publicRecord,
      onlyMissing,
    );
    if (arv.didApply) {
      didApply = true;
      nextProperty = {
        ...nextProperty,
        editableData: {
          ...nextProperty.editableData,
          arv: {
            ...subjectEditable.arv,
            improvements: arv.improvements,
          },
        } as PropertyData['editableData'],
        selectedSources: {
          ...nextProperty.selectedSources,
          editableData: {
            ...nextProperty.selectedSources?.editableData,
            arv: {
              ...nextProperty.selectedSources?.editableData?.arv,
              improvements: arv.selectedSources,
            },
          },
        },
      };
    }
  }

  return didApply ? nextProperty : property;
};
